import { Tooltip, TooltipProps } from "@mui/material";
import Box from "@mui/material/Box";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import styles from "./BriaTooltip.module.scss";

export type BriaTooltipProps = {
	displayOn?: "hover" | "click";
	isHtmlTitle?: boolean;
	disable?: boolean;
	keepToolTipOnHover?: boolean;
	noWrapper?: boolean;
	alwaysShown?: boolean;
	tooltipClassName?: string;
	arrowClassName?: string;
	mode?: "light" | "dark";
} & TooltipProps;

const BriaTooltip = ({
	displayOn = "hover",
	keepToolTipOnHover = false,
	placement = "top",
	enterDelay = 100,
	leaveDelay = 50,
	arrow = true,
	className,
	children,
	isHtmlTitle = true,
	title,
	disable = false,
	noWrapper = false,
	alwaysShown = false,
	arrowClassName,
	tooltipClassName,
	mode = "light",
	...rest
}: BriaTooltipProps) => {
	const [open, setOpen] = useState(false);
	const [dismissedByClick, setDismissedByClick] = useState(false);
	const tooltipRef = useRef<HTMLDivElement>(null);

	const hasTitle = typeof title === "string" ? title.length > 0 : Boolean(title);
	const alwaysShownOpen = alwaysShown && hasTitle && !dismissedByClick;

	useEffect(() => {
		if (alwaysShown && hasTitle) {
			setDismissedByClick(false);
		}
	}, [alwaysShown, title, hasTitle]);

	const handleTooltipToggle = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		setOpen((prev) => !prev);
	};

	const handleTooltipClose = () => {
		setOpen(false);
	};

	useEffect(() => {
		if (displayOn === "click" || keepToolTipOnHover) {
			const handleClickOutside = (event: MouseEvent) => {
				if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
					if ((event.target as HTMLElement).tagName.toLowerCase() === "a") {
						return;
					}
					handleTooltipClose();
				}
			};
			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
	}, [displayOn]);

	const handleAlwaysShownClick = () => {
		setDismissedByClick(true);
	};

	const eventHandlers = alwaysShown
		? { onClick: handleAlwaysShownClick }
		: displayOn === "click"
			? { onClick: handleTooltipToggle }
			: {
					onMouseEnter: () => hasTitle && setOpen(true),
					onMouseLeave: !keepToolTipOnHover ? handleTooltipClose : undefined,
					onClick: handleTooltipClose,
					onMouseDown: () => {
						setTimeout(() => handleTooltipClose(), 0);
					},
				};

	// Determine final open state - never show if no title
	const isOpen = hasTitle && (alwaysShown ? alwaysShownOpen : open);

	return !disable ? (
		<Tooltip
			{...rest}
			title={
				isHtmlTitle ? <Box component="div" dangerouslySetInnerHTML={{ __html: title as string }}></Box> : title
			}
			placement={placement}
			enterDelay={alwaysShown ? 0 : enterDelay}
			leaveDelay={leaveDelay}
			arrow={arrow}
			{...(noWrapper ? {} : { open: isOpen })}
			disableHoverListener={rest.disableHoverListener || displayOn === "click" || alwaysShown}
			className={className}
			componentsProps={{
				...rest.componentsProps,
				tooltip: {
					...rest.componentsProps?.tooltip,
					className: clsx(rest.componentsProps?.tooltip?.className, styles.tooltip, tooltipClassName, {
						[styles.dark]: mode === "dark",
					}),
				},
				arrow: {
					...rest.componentsProps?.arrow,
					className: clsx(rest.componentsProps?.arrow?.className, styles.arrow, arrowClassName, {
						[styles.dark]: mode === "dark",
					}),
				},
				popper: {
					...rest.componentsProps?.popper,
					className: clsx(rest.componentsProps?.popper?.className, className),
				},
			}}
		>
			{noWrapper ? (
				children
			) : (
				<Box
					className={clsx(styles.tooltipChild, { [styles.clickable]: displayOn == "click" })}
					ref={tooltipRef}
					{...eventHandlers}
				>
					{children}
				</Box>
			)}
		</Tooltip>
	) : (
		children
	);
};

export default BriaTooltip;
